export const IDLE_TIMEOUT_MS = 60 * 60 * 1000
export const IDLE_WARNING_MS = 60 * 1000
export const ACTIVITY_WRITE_THROTTLE_MS = 30 * 1000
export const CLOCK_SKEW_TOLERANCE_MS = 5 * 1000

export type IdlePhase = 'active' | 'warning' | 'expired'

export interface IdlePolicy {
  readonly timeoutMs: number
  readonly warningMs: number
  readonly writeThrottleMs: number
}

export const DEFAULT_IDLE_POLICY: IdlePolicy = Object.freeze({
  timeoutMs: IDLE_TIMEOUT_MS,
  warningMs: IDLE_WARNING_MS,
  writeThrottleMs: ACTIVITY_WRITE_THROTTLE_MS,
})

export interface IdleVerdict {
  readonly phase: IdlePhase
  readonly msUntilExpiry: number
}

const EXPIRED: IdleVerdict = Object.freeze({ phase: 'expired', msUntilExpiry: 0 })

export function evaluateIdle(
  lastActivityAt: number | null,
  now: number,
  policy: IdlePolicy = DEFAULT_IDLE_POLICY,
): IdleVerdict {
  if (lastActivityAt === null || !Number.isFinite(lastActivityAt)) return EXPIRED

  const elapsed = now - lastActivityAt
  if (elapsed < -CLOCK_SKEW_TOLERANCE_MS) return EXPIRED

  const remaining = policy.timeoutMs - Math.max(elapsed, 0)
  if (remaining <= 0) return EXPIRED
  if (remaining <= policy.warningMs) return { phase: 'warning', msUntilExpiry: remaining }
  return { phase: 'active', msUntilExpiry: remaining }
}

export function shouldPersistActivity(
  lastWriteAt: number | null,
  now: number,
  throttleMs: number = ACTIVITY_WRITE_THROTTLE_MS,
): boolean {
  if (lastWriteAt === null) return true
  return now - lastWriteAt >= throttleMs || now < lastWriteAt
}
